/**
 * Reading a stored scribe transcript.
 *
 * Kept apart from the component so it can be tested directly: this is where the
 * risk lives, not in the markup. A transcript is read back from S3 and is not
 * one shape:
 *
 *   - HealthScribe batch output (session.hs_transcript_key, the usual case):
 *     { Conversation: { TranscriptSegments: [ { Content, BeginAudioTime,
 *       ParticipantDetails: { ParticipantRole: 'CLINICIAN_0' } } ] } }
 *   - the streaming shape built in AmbientScribeService, if a practice-scoped
 *     copy was written: { segments: [ { text, role, startTime } ] }
 *
 * Getting this wrong renders an empty panel, which is indistinguishable to the
 * reader from "no transcript was recorded" — so an unrecognised shape returns
 * null and the component offers the raw JSON instead of showing nothing.
 */
/**
 * Flatten a stored transcript to [{ id, role, text, startTime }].
 * Returns [] for a recognised-but-empty transcript and null for a shape we do
 * not understand, so the caller can tell those two apart.
 */
export function normaliseSegments(transcript) {
  if (!transcript || typeof transcript !== 'object') return null;

  const raw =
    transcript.Conversation?.TranscriptSegments ??
    transcript.TranscriptSegments ??
    transcript.segments ??
    (Array.isArray(transcript) ? transcript : null);

  if (!Array.isArray(raw)) return null;

  return raw
    .map((seg, idx) => {
      const text = (seg.Content ?? seg.text ?? seg.content ?? '').toString().trim();
      if (!text) return null;
      return {
        id: seg.SegmentId ?? seg.segmentId ?? idx,
        role: normaliseRole(seg.ParticipantDetails?.ParticipantRole ?? seg.ParticipantRole ?? seg.role ?? seg.channelId ?? seg.ChannelId),
        text,
        startTime: firstNumber(seg.BeginAudioTime, seg.startTime, seg.beginAudioTime),
      };
    })
    .filter(Boolean);
}

/** CLINICIAN_0 / clinician / channel 0 all mean the doctor; everything else is the patient. */
function normaliseRole(value) {
  if (value === 0 || value === '0') return 'clinician';
  return String(value ?? '').toUpperCase().startsWith('CLINICIAN') ? 'clinician' : 'patient';
}

function firstNumber(...values) {
  for (const v of values) {
    const n = Number(v);
    if (v !== null && v !== undefined && v !== '' && Number.isFinite(n)) return n;
  }
  return null;
}
